// 화면 그리기(§9.8) — 캔버스 렌더. 뷰어 겹침 캔버스와 원격지 그림판 창이 **같은 함수**로 그린다
// (한쪽만 화살촉 모양·형광펜 농도가 달라지는 일이 없게).
import { arrowWings, bbox, scalePx, TEXT_LINE_HEIGHT } from './geometry.ts';
import type { Space } from './geometry.ts';
import type { Author, DrawDoc, DrawObj, ShapeObj, StrokeObj, TextObj } from './model.ts';
import type { DrawEditor } from './editor.ts';
import { BOARD_BG, FILL_ALPHA, LASER_COLOR, LASER_FADE_MS, MARKER_ALPHA } from './style.ts';

/** 글자 글꼴(한글이 고르게 보이는 순서). 적중 판정의 어림 폭(geometry.ts)과 맞춘 굵기다. */
export const TEXT_FONT_FAMILY = `'Malgun Gothic', 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif`;
/** 선택 표시 색. */
const SELECT_COLOR = '#2b8cff';
/** 선택 상자 바깥 여백(px). */
const SELECT_PAD = 6;

/**
 * 캔버스 전체를 다시 그린다.
 * @param s 그리는 영역의 CSS 크기(px). 캔버스 픽셀 = s × dpr.
 */
export function renderEditor(
  ctx: CanvasRenderingContext2D,
  ed: DrawEditor,
  s: Space,
  dpr: number,
  opts: { now?: number; hideAuthor?: Author | null } = {},
): void {
  const now = opts.now ?? Date.now();
  const hide = hiddenAuthor(ed.doc, opts.hideAuthor);
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, s.w, s.h);
  drawDocument(ctx, ed.doc, s, opts.hideAuthor);
  if (ed.draft) drawObj(ctx, ed.draft, s);
  for (const id of ed.selection) {
    const o = ed.doc.get(id);
    if (o) drawSelection(ctx, o, s);
  }
  for (const [by, pts] of ed.lasers) if (by !== hide) drawLaser(ctx, pts, s, now);
}

/**
 * 그리지 않을 작성자.
 * 🔴 캡처 제외가 안 되는 원격지(§9.8.1)에서는 원격지 그림이 **영상에 이미 찍혀 온다** — 캔버스에도 그리면 두 번 보인다.
 *    문서에는 그대로 두고(되돌리기·지우개가 맞게 돌도록) 그리기만 건너뛴다. 흰 판은 예외 — 영상에는 흰 판이 없다.
 */
function hiddenAuthor(doc: DrawDoc, hideAuthor: Author | null | undefined): Author | null {
  return doc.board ? null : hideAuthor ?? null;
}

/**
 * 문서만 그린다(흰 판 바탕 + 보이는 객체) — 선택 표시·레이저·그리는 중인 획은 없다.
 * 화면(`renderEditor`)과 PNG 저장(`snapshot.ts`)이 **같은 함수**를 쓴다(저장본이 화면과 달라지지 않게).
 * 좌표계는 호출자가 맞춰 둔다(`s` = 그리는 영역 크기).
 */
export function drawDocument(ctx: CanvasRenderingContext2D, doc: DrawDoc, s: Space, hideAuthor?: Author | null): void {
  const hide = hiddenAuthor(doc, hideAuthor);
  if (doc.board) {
    ctx.fillStyle = BOARD_BG;
    ctx.fillRect(0, 0, s.w, s.h);
  }
  for (const o of doc.visible()) if (o.by !== hide) drawObj(ctx, o, s);
}

export function drawObj(ctx: CanvasRenderingContext2D, o: DrawObj, s: Space): void {
  ctx.save();
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.strokeStyle = o.color;
  ctx.fillStyle = o.color;
  ctx.lineWidth = Math.max(1, scalePx(o.width, s));
  switch (o.kind) {
    case 'pen':
    case 'marker':
      drawStroke(ctx, o, s);
      break;
    case 'line':
    case 'arrow':
      drawLine(ctx, o, s);
      break;
    case 'rect':
    case 'ellipse':
      drawBox(ctx, o, s);
      break;
    case 'text':
      drawText(ctx, o, s);
      break;
  }
  ctx.restore();
}

/**
 * 자유 곡선 — 점 사이를 중점 기준 2차 곡선으로 잇는다(꺾인 선이 아니라 매끈하게).
 * 형광펜은 **경로 하나로** 한 번에 칠한다 — 선분마다 칠하면 겹치는 이음매가 진하게 뭉친다.
 */
function drawStroke(ctx: CanvasRenderingContext2D, o: StrokeObj, s: Space): void {
  const p = o.pts;
  if (o.kind === 'marker') ctx.globalAlpha = MARKER_ALPHA;
  if (p.length === 2) {
    ctx.beginPath();
    ctx.arc(p[0] * s.w, p[1] * s.h, ctx.lineWidth / 2, 0, Math.PI * 2);
    ctx.fill();
    return;
  }
  ctx.beginPath();
  ctx.moveTo(p[0] * s.w, p[1] * s.h);
  for (let i = 2; i + 2 < p.length; i += 2) {
    const mx = ((p[i] + p[i + 2]) / 2) * s.w;
    const my = ((p[i + 1] + p[i + 3]) / 2) * s.h;
    ctx.quadraticCurveTo(p[i] * s.w, p[i + 1] * s.h, mx, my);
  }
  ctx.lineTo(p[p.length - 2] * s.w, p[p.length - 1] * s.h);
  ctx.stroke();
}

function drawLine(ctx: CanvasRenderingContext2D, o: ShapeObj, s: Space): void {
  const ax = o.a[0] * s.w;
  const ay = o.a[1] * s.h;
  const bx = o.b[0] * s.w;
  const by = o.b[1] * s.h;
  ctx.beginPath();
  ctx.moveTo(ax, ay);
  ctx.lineTo(bx, by);
  ctx.stroke();
  if (o.kind === 'arrow' && Math.hypot(bx - ax, by - ay) > 1) {
    const [w1, w2] = arrowWings(ax, ay, bx, by, ctx.lineWidth);
    ctx.beginPath();
    ctx.moveTo(bx, by);
    ctx.lineTo(w1[0], w1[1]);
    ctx.lineTo(w2[0], w2[1]);
    ctx.closePath();
    ctx.fill();
    ctx.stroke(); // 둥근 이음으로 촉 끝을 부드럽게
  }
}

function drawBox(ctx: CanvasRenderingContext2D, o: ShapeObj, s: Space): void {
  const x = Math.min(o.a[0], o.b[0]) * s.w;
  const y = Math.min(o.a[1], o.b[1]) * s.h;
  const w = Math.abs(o.b[0] - o.a[0]) * s.w;
  const h = Math.abs(o.b[1] - o.a[1]) * s.h;
  ctx.beginPath();
  if (o.kind === 'rect') {
    ctx.rect(x, y, w, h);
  } else {
    ctx.ellipse(x + w / 2, y + h / 2, w / 2, h / 2, 0, 0, Math.PI * 2);
  }
  if (o.fill) {
    ctx.globalAlpha = FILL_ALPHA;
    ctx.fill();
    ctx.globalAlpha = 1;
  }
  ctx.stroke();
}

/** 밝은 색인가(글자 테두리 색을 고른다 — 어떤 바탕 위에서도 읽히게). */
function isLight(hex: string): boolean {
  const n = parseInt(hex.slice(1), 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return 0.299 * r + 0.587 * g + 0.114 * b > 150;
}

function drawText(ctx: CanvasRenderingContext2D, o: TextObj, s: Space): void {
  const size = scalePx(o.width, s);
  ctx.font = `600 ${size}px ${TEXT_FONT_FAMILY}`;
  ctx.textBaseline = 'top';
  ctx.lineWidth = Math.max(2, size / 8);
  ctx.strokeStyle = isLight(o.color) ? 'rgba(0,0,0,0.65)' : 'rgba(255,255,255,0.8)';
  const x = o.at[0] * s.w;
  let y = o.at[1] * s.h;
  for (const line of o.text.split('\n')) {
    ctx.strokeText(line, x, y);
    ctx.fillText(line, x, y);
    y += size * TEXT_LINE_HEIGHT;
  }
}

function drawSelection(ctx: CanvasRenderingContext2D, o: DrawObj, s: Space): void {
  const [x0, y0, x1, y1] = bbox(o, s);
  const pad = SELECT_PAD + scalePx(o.width, s) / 2;
  ctx.save();
  ctx.strokeStyle = SELECT_COLOR;
  ctx.lineWidth = 1.5;
  ctx.setLineDash([6, 4]);
  ctx.strokeRect(x0 * s.w - pad, y0 * s.h - pad, (x1 - x0) * s.w + pad * 2, (y1 - y0) * s.h + pad * 2);
  ctx.restore();
}

/** 레이저 꼬리 — 오래된 점일수록 흐리고 가늘게, 맨 앞은 빛나는 점. */
function drawLaser(ctx: CanvasRenderingContext2D, pts: { x: number; y: number; t: number }[], s: Space, now: number): void {
  if (!pts.length) return;
  ctx.save();
  ctx.lineCap = 'round';
  ctx.strokeStyle = LASER_COLOR;
  for (let i = 1; i < pts.length; i++) {
    const life = 1 - (now - pts[i].t) / LASER_FADE_MS;
    if (life <= 0) continue;
    ctx.globalAlpha = life;
    ctx.lineWidth = 2 + 4 * life;
    ctx.beginPath();
    ctx.moveTo(pts[i - 1].x * s.w, pts[i - 1].y * s.h);
    ctx.lineTo(pts[i].x * s.w, pts[i].y * s.h);
    ctx.stroke();
  }
  const head = pts[pts.length - 1];
  const life = 1 - (now - head.t) / LASER_FADE_MS;
  if (life > 0) {
    ctx.globalAlpha = life;
    ctx.fillStyle = LASER_COLOR;
    ctx.shadowColor = LASER_COLOR;
    ctx.shadowBlur = 12;
    ctx.beginPath();
    ctx.arc(head.x * s.w, head.y * s.h, 6, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}
